import { useEffect, useState } from 'react';

// Append-only label accumulator: remembers labels for every {value, label} pair
// ever observed, so consumers can resolve a selected value's label even after
// it has been filtered out of the current input. Encapsulates the state+effect
// pattern so callers don't reimplement it.
export const useLabelCache = <T, I>(
  items: readonly I[],
  getValue: (item: I) => T,
  getLabel: (item: I) => string
): Map<T, string> => {
  const [cache, setCache] = useState<Map<T, string>>(new Map());
  useEffect(() => {
    setCache(prev => {
      const next = new Map(prev);
      let changed = false;
      for (const item of items) {
        const v = getValue(item);
        const l = getLabel(item);
        if (next.get(v) !== l) {
          next.set(v, l);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
    // getValue / getLabel are stable across renders by convention; we only react
    // to items identity changing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);
  return cache;
};
